import type { Move } from '../types/battle';
import { MOVE_TEMPLATES } from '../types/battle';
import { getMoveNameSync } from './moveOverrides';

/**
 * `battleMoves` documents use stable ids `move_1` … `move_N` aligned with `MOVE_TEMPLATES` indices.
 * When `category` / `manifestType` are missing on a row (legacy writes, partial merges), we can still
 * tell manifest vs elemental from the template slot.
 */
export function templateIndexFromMoveStableId(moveId?: string): number | null {
  if (!moveId || typeof moveId !== 'string') return null;
  const m = /^move_(\d+)$/i.exec(moveId.trim());
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n - 1;
}

export function moveMatchesKnownManifestTemplate(
  move: Pick<Move, 'id' | 'name' | 'category' | 'manifestType'>
): boolean {
  if (move.category === 'manifest') return true;
  if (move.manifestType) return true;
  const idx = templateIndexFromMoveStableId(move.id);
  if (idx !== null) {
    const template = MOVE_TEMPLATES[idx];
    if (template?.category === 'manifest') return true;
  }
  // Live loadouts / copies may use non–move_N ids but keep the canonical template name (or admin override).
  const raw = (move.name || '').trim();
  if (!raw) return false;
  const resolved = (getMoveNameSync(raw) || '').trim();
  for (const t of MOVE_TEMPLATES) {
    if (t.category !== 'manifest') continue;
    if (t.name === raw || t.name === resolved) return true;
    const canon = (getMoveNameSync(t.name) || '').trim();
    if (canon && (canon === raw || canon === resolved)) return true;
  }
  return false;
}

export function moveMatchesKnownElementalTemplate(
  move: Pick<Move, 'id' | 'name' | 'category' | 'elementalAffinity'>
): boolean {
  if (move.category === 'elemental') return true;
  const idx = templateIndexFromMoveStableId(move.id);
  if (idx !== null) {
    const template = MOVE_TEMPLATES[idx];
    if (template?.category === 'elemental') return true;
  }
  const raw = (move.name || '').trim();
  if (!raw) return false;
  const resolved = (getMoveNameSync(raw) || '').trim();
  for (const t of MOVE_TEMPLATES) {
    if (t.category !== 'elemental') continue;
    if (t.name === raw || t.name === resolved) return true;
    const canon = (getMoveNameSync(t.name) || '').trim();
    if (canon && (canon === raw || canon === resolved)) return true;
  }
  return false;
}

/** Applied to stored challenge rewardPP / rewardXP / rewardTruthMetal for display and grants. */
export const DAILY_CHALLENGE_REWARD_MULTIPLIER = 10;

export function scaledDailyChallengeRewardPP(raw: number | undefined): number {
  return Math.max(0, Math.floor((raw ?? 0) * DAILY_CHALLENGE_REWARD_MULTIPLIER));
}

export function scaledDailyChallengeRewardXP(raw: number | undefined): number {
  return Math.max(0, Math.floor((raw ?? 0) * DAILY_CHALLENGE_REWARD_MULTIPLIER));
}

export function scaledDailyChallengeRewardTruthMetal(raw: number | undefined): number {
  return Math.max(0, Math.floor((raw ?? 0) * DAILY_CHALLENGE_REWARD_MULTIPLIER));
}

const NUMBER_WORD_MAP: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

/**
 * Prefer a target parsed from the challenge title (e.g. "THREE (3)") so UI and Firestore progress
 * stay aligned when the stored `target` field is wrong.
 */
export function getEffectiveDailyChallengeTarget(challenge: { title?: string; target?: number }): number {
  const title = challenge.title ?? '';

  const parenMatch = title.match(/\((\d+)\)/);
  if (parenMatch) {
    const extractedTarget = parseInt(parenMatch[1], 10);
    if (extractedTarget > 0) return extractedTarget;
  }

  const titleLower = title.toLowerCase();
  for (const [word, num] of Object.entries(NUMBER_WORD_MAP)) {
    if (titleLower.includes(word)) {
      const digitMatch = title.match(/\b(\d+)\b/);
      if (digitMatch && parseInt(digitMatch[1], 10) === num) {
        return num;
      }
    }
  }

  const stored = challenge.target;
  if (typeof stored === 'number' && Number.isFinite(stored) && stored > 0) {
    return Math.floor(stored);
  }
  return 1;
}

export function normalizeDailyChallengeTypeKey(t: string): string {
  return String(t)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_');
}

/**
 * Maps event keys from `updateChallengeProgressByType` to normalized admin/Firestore type variants.
 */
const EVENT_CHALLENGE_SYNONYMS: Record<string, string[]> = {
  defeat_enemies: ['defeat_enemy', 'defeat_opponents', 'kill_enemies', 'enemies_defeated', 'defeat', 'defeat_enemy_units'],
  use_elemental_move: [
    'use_elemental_moves',
    'elemental_move',
    'elemental_moves',
    'elemental',
    'use_element',
    'use_3_elemental_moves',
    'three_elemental_moves',
  ],
  use_manifest_ability: [
    'use_manifest_abilities',
    'manifest_ability',
    'manifest_abilities',
    'manifest',
    'use_manifest',
    'use_your_manifest_abilities',
  ],
  attack_vault: ['vault_attack', 'attack_a_vault', 'attack_vaults'],
  use_action_card: ['use_action_cards', 'action_card', 'action_cards'],
  win_battle: ['win_battles', 'win_a_battle', 'battle_win'],
  earn_pp: ['earn_power_points', 'earn_pp_total', 'gain_pp'],
  /** Profile XP (`students.xp`) — see `playerProgressionRewards.grantPlayerProfileXp` / `mirrorProfileXpToProgressionSystems`. */
  earn_xp: [
    'earn_experience',
    'gain_xp',
    'gain_experience',
    'earn_profile_xp',
    'xp_earned',
    'collect_xp',
  ],
  /** Counts once per finalized Live Event session for roster participants (see `finalizeSessionStats`). */
  participate_live_event: [
    'live_event',
    'live_events',
    'join_live_event',
    'live_session',
    'in_session',
    'class_session',
  ],
  use_health_potion: ['use_health_potions', 'health_potion', 'use_potion'],
};

export function dailyChallengeStoredTypeMatchesEvent(
  storedType: string | undefined,
  eventChallengeType: string
): boolean {
  if (!storedType) return false;
  const ns = normalizeDailyChallengeTypeKey(storedType);
  const ne = normalizeDailyChallengeTypeKey(eventChallengeType);
  if (ns === ne) return true;
  const synonyms = EVENT_CHALLENGE_SYNONYMS[ne];
  if (synonyms?.some((s) => normalizeDailyChallengeTypeKey(s) === ns)) return true;

  // Admin text / legacy: full sentence saved as `type`, or title pasted into type field
  if (ne === 'use_manifest_ability' && ns.includes('manifest')) return true;
  if (ne === 'use_elemental_move' && ns.includes('elemental')) return true;
  if (ne === 'use_health_potion' && (ns.includes('health') || ns.includes('potion'))) return true;
  if (ne === 'defeat_enemies' && (ns.includes('defeat') || ns.includes('enemy'))) return true;
  if (ne === 'win_battle' && (ns.includes('win') && ns.includes('battle'))) return true;
  if (ne === 'attack_vault' && ns.includes('vault')) return true;
  if (ne === 'earn_pp' && (ns.includes('earn_pp') || ns.includes('power_point'))) return true;
  if (ne === 'earn_xp' && (ns.includes('earn_xp') || ns.includes('experience') || ns.includes('xp'))) {
    // Avoid matching "Use your Manifest Abilities…" which does not contain earn_xp tokens above alone —
    // "experience" in unrelated titles is rare; keep as-is.
    if (!ns.includes('manifest') && !ns.includes('elemental')) return true;
  }

  return false;
}

/**
 * Match a player's assigned challenge to a gameplay event.
 * Uses stored type, admin challenge type, and title/description so `custom`
 * challenges like "Use your Manifest Abilities THREE (3) Times" still increment.
 */
export function dailyChallengeMatchesGameplayEvent(
  opts: {
    storedType?: string;
    detailsType?: string;
    title?: string;
    description?: string;
  },
  eventChallengeType: string
): boolean {
  const { storedType, detailsType, title, description } = opts;
  if (dailyChallengeStoredTypeMatchesEvent(storedType, eventChallengeType)) return true;
  if (dailyChallengeStoredTypeMatchesEvent(detailsType, eventChallengeType)) return true;
  if (dailyChallengeStoredTypeMatchesEvent(title, eventChallengeType)) return true;
  if (dailyChallengeStoredTypeMatchesEvent(description, eventChallengeType)) return true;

  // Explicit custom type: infer only from title/description text
  const st = normalizeDailyChallengeTypeKey(storedType || detailsType || '');
  if (st === 'custom' || st === '') {
    const blob = normalizeDailyChallengeTypeKey(`${title || ''} ${description || ''}`);
    if (!blob) return false;
    if (eventChallengeType === 'use_manifest_ability' && blob.includes('manifest')) return true;
    if (eventChallengeType === 'use_elemental_move' && blob.includes('elemental')) return true;
    if (
      eventChallengeType === 'use_health_potion' &&
      (blob.includes('health') || blob.includes('potion'))
    ) {
      return true;
    }
    if (
      eventChallengeType === 'defeat_enemies' &&
      (blob.includes('defeat') || blob.includes('enemy'))
    ) {
      return true;
    }
    if (eventChallengeType === 'win_battle' && blob.includes('win') && blob.includes('battle')) {
      return true;
    }
    if (eventChallengeType === 'attack_vault' && blob.includes('vault')) return true;
  }
  return false;
}

const MANIFEST_NAME_SNIPPETS = [
  'read the room',
  'emotional read',
  'pattern shield',
  'team read',
  'environment read',
  'reality rewrite',
  'narrative barrier',
  'story weave',
  'world rewrite',
  'illusion strike',
  'mirage shield',
  'visual deception',
  'reality illusion',
  'flow strike',
  'rhythm guard',
  'team flow',
  'athletic mastery',
  'harmonic blast',
  'melody shield',
  'chorus power',
  'song of power',
  'pattern break',
  'strategy matrix',
  'game mastery',
  'ultimate strategy',
  'precision strike',
  'memory shield',
  'perfect observation',
  'omniscient view',
  'emotional resonance',
  'empathic barrier',
  'group empathy',
  'universal connection',
  'tool strike',
  'construct shield',
  'creative mastery',
  'divine creation',
  'energy feast',
  'nourishing barrier',
  'feast of power',
  'divine nourishment',
  // Display / VFX / override aliases (getMoveNameSync) — still manifest-branch skills
  'room scan',
  'self read',
  'narrative weave',
  'strike counter',
  'foresight',
  'evasive calibration',
  'system redirect',
];

export function moveCountsForDailyElementalChallenge(
  move: Pick<Move, 'category' | 'elementalAffinity' | 'manifestType' | 'id' | 'effectKey' | 'name'>
): boolean {
  return classifyMoveForDailyChallenge(move) === 'elemental';
}

export function moveCountsForDailyManifestChallenge(
  move: Pick<
    Move,
    | 'category'
    | 'manifestType'
    | 'id'
    | 'name'
    | 'rrCandySkillId'
    | 'rrCandyNodeId'
    | 'effectKey'
    | 'elementalAffinity'
  >
): boolean {
  return classifyMoveForDailyChallenge(move) === 'manifest';
}

/**
 * Mutually exclusive daily-challenge skill class.
 * A single skill use must never credit both Manifest and Elemental challenges.
 * Priority: explicit manifest / RR / L2 → explicit elemental category / template → affinity fallback.
 */
export function classifyMoveForDailyChallenge(
  move: Pick<
    Move,
    | 'category'
    | 'manifestType'
    | 'elementalAffinity'
    | 'id'
    | 'name'
    | 'rrCandySkillId'
    | 'rrCandyNodeId'
    | 'effectKey'
  >
): 'manifest' | 'elemental' | null {
  if (move.effectKey === 'level2_manifest') return 'manifest';
  if (move.rrCandySkillId || move.rrCandyNodeId) return 'manifest';
  const id = move.id || '';
  if (id.startsWith('rr-candy') || id.startsWith('rrCandy')) return 'manifest';
  const idLower = id.toLowerCase();
  if (idLower.startsWith('l2-manifest::') || idLower.includes('manifest-')) return 'manifest';

  if (move.category === 'manifest' || move.manifestType) return 'manifest';
  if (typeof move.category === 'string' && move.category.toLowerCase() === 'manifest') return 'manifest';
  if (moveMatchesKnownManifestTemplate(move)) return 'manifest';

  const raw = (move.name || '').trim();
  const resolved = raw ? (getMoveNameSync(raw) || '').trim() : '';
  const combined = `${raw} ${resolved}`.toLowerCase();
  if (combined && MANIFEST_NAME_SNIPPETS.some((p) => combined.includes(p))) return 'manifest';

  // Elemental only after manifest is ruled out
  if (move.category === 'elemental') return 'elemental';
  if (typeof move.category === 'string' && move.category.toLowerCase() === 'elemental') return 'elemental';
  if (moveMatchesKnownElementalTemplate(move)) return 'elemental';
  // Affinity with an explicit non-empty category (manifest already returned above).
  if (move.elementalAffinity && move.category) return 'elemental';

  return null;
}

/** At most one challenge type for a skill use (never both manifest + elemental). */
export function challengeTypesForSkillUse(
  move: Pick<
    Move,
    | 'category'
    | 'manifestType'
    | 'elementalAffinity'
    | 'id'
    | 'name'
    | 'rrCandySkillId'
    | 'rrCandyNodeId'
    | 'effectKey'
  >
): Array<'use_manifest_ability' | 'use_elemental_move'> {
  const kind = classifyMoveForDailyChallenge(move);
  if (kind === 'manifest') return ['use_manifest_ability'];
  if (kind === 'elemental') return ['use_elemental_move'];
  return [];
}
